import type { Emitter } from "@core/events/emitter";

/**
 * One row of the asset library grid — the web equivalent of Unity's
 * `AssetPreviewViewModel` fields, resolved from an `AssetDto` plus the
 * account-wide dependent-projects map.
 */
export interface AssetLibraryItem {
  readonly id: string;
  /** `AssetData.FileName` (persisted under the Dynamo attr `Name`). */
  readonly name: string;
  /** S3 object suffix (`assetObjectKey(dto)`), needed for the delete cascade. */
  readonly objectKey: string;
  readonly format: string;
  readonly scale: number;
  readonly fileSize: number;
  readonly triangleCount: number;
  /** Uploader's name tag (`AssetData.UserTag`). */
  readonly userTag: string;
  /** Parsed `AssetData.LastModified` (epoch ms; 0 when unparseable). */
  readonly lastModifiedMs: number;
  /**
   * Cache key for this asset's S3 preview, `"{id}|{lastModifiedMs}"`.
   *
   * The preview object is overwritten IN PLACE at `assetpreview/{assetId}`
   * every time the asset is re-saved to the library, so an id-only cache would
   * keep serving the stale `blob:` URL after a Save to Library. Folding
   * `LastModified` into the key is the same invalidation strategy
   * `models/storage/thumbnailCache.ts` uses for scene / project cards.
   */
  readonly previewCacheKey: string;
  /** Names of every account project referencing this asset. */
  readonly dependentProjectNames: ReadonlyArray<string>;
  /**
   * Unity `AssetPreviewViewModel.CanRemove` — an asset used by ANY project
   * cannot be deleted (`CustomerModel.DeleteAssetAsync` throws otherwise).
   */
  readonly canRemove: boolean;
}

/**
 * `IAssetLibraryModel` — the account's saved-asset catalog behind the
 * Asset Library panel.
 *
 * **Ownership deviation from Unity, deliberate:** Unity keeps this on
 * `CustomerModel` (`LoadAssetsAsync` / `Assets` / `GetDependentProjects` /
 * `DeleteAssetAsync`). The web `ICustomerModel` is a thin GraphQL
 * `getAccount` wrapper for the account NAME only, so folding a Dynamo +
 * S3 catalog into it would blur that class's single responsibility. The
 * behavior, method names, and quirks below are ported verbatim; only the
 * host class differs.
 */
export interface IAssetLibraryModel {
  /** Emitted whenever the item list changes (load, insert, delete). */
  readonly onChanged: Emitter<ReadonlyArray<AssetLibraryItem>>;
  /** Emitted around the initial catalog fetch (`AssetLibraryViewModel.IsBusy`). */
  readonly onBusyChanged: Emitter<boolean>;

  readonly items: ReadonlyArray<AssetLibraryItem>;
  readonly isBusy: boolean;

  /**
   * Port of `CustomerModel.LoadAssetsAsync` + the library VM's build step:
   * fetch every asset row, drop `AssetVersion === 0` (never saved to the
   * library) and `HasLicensingRestrictions` rows, sort by `LastModified`
   * DESCENDING, and join the dependent-projects map from the account's
   * project rows. Never throws — an error yields an empty list.
   */
  loadAsync(signal?: AbortSignal): Promise<ReadonlyArray<AssetLibraryItem>>;

  /**
   * Resolve (and cache) the asset's preview PNG blob URL, or `null` when
   * the object doesn't exist. Unity loads these in batches of 10.
   */
  /**
   * Resolve the asset's S3 preview as a `blob:` URL. Pass the item's
   * {@link AssetLibraryItem.previewCacheKey} so a re-saved asset refetches
   * instead of serving the previous version's bytes; omitting it caches on the
   * asset id alone.
   */
  getPreviewUrlAsync(assetId: string, cacheKey?: string): Promise<string | null>;

  /**
   * Port of `ProjectModel.DeleteAssetFromAssetLibraryAsync` +
   * `CustomerModel.DeleteAssetAsync`: refuses when the asset is used by any
   * project, else deletes the part rows, the asset row, the S3 binary, and
   * the S3 preview (missing objects tolerated), then drops the item.
   *
   * Web deviations: no storage-aggregate GraphQL delta (Unity decrements it
   * twice — a known upstream double-count; the web has no admin-storage
   * model), and no duplicated-material / orphan-texture row sweep for
   * `AssetVersion > 0` assets (orphan rows are Unity-tolerated debt, same
   * as the existing import-path note).
   */
  deleteAsync(assetId: string): Promise<void>;

  /** Drop cached state (sign-out / project switch). Revokes preview URLs. */
  reset(): void;
}

export const ASSET_LIBRARY_MODEL = Symbol.for("Campfire.AssetLibraryModel");
